import type { RichTextEvent } from "@library-elements/text";
import type { Command } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import {
  applyLink,
  clearFormatting,
  deleteRkpi,
  insertAiText,
  insertEmoji,
  insertIcon,
  removeLink,
  saveRkpi,
  setAiClassification,
  setAlignment,
  setColor,
  setFontSize,
  setHighlight,
  setLineHeight,
  setList,
  setMarkOnElementOrSelection,
  setTextAnimation,
  setTextDirection,
  setTextStyle,
  type ListChoice,
} from "../commands";
import { textSchema, type Alignment } from "../schema";
import { toTextStyleId } from "../typography";
import { insertVariableToken } from "../variables";
import { namespaceInsertedToken } from "../../froala/toolbar-handlers/namespace-inserted-token";

const run = (view: EditorView, command: Command) => {
  command(view.state, view.dispatch, view);
};

/**
 * Routes the toolbar's RichTextEvent vocabulary (the same events the Froala
 * toolbar emits over RichTextSubject) onto ProseMirror commands. Every
 * control of the shared toolbar is handled here; "refresh", "ai" and
 * "onFroalaDestroy" are editor-level and handled in ProseMirrorText.
 */
export const handleToolbarEvent = (
  view: EditorView,
  event: RichTextEvent,
): void => {
  const { marks } = textSchema;
  switch (event.type) {
    case "setBold":
      return run(view, setMarkOnElementOrSelection(marks.strong));
    case "setItalic":
      return run(view, setMarkOnElementOrSelection(marks.em));
    case "setUnderline":
      return run(view, setMarkOnElementOrSelection(marks.underline));
    case "setStrikethrough":
      return run(view, setMarkOnElementOrSelection(marks.strike));
    case "subscript":
      return run(view, setMarkOnElementOrSelection(marks.subscript));
    case "superscript":
      return run(view, setMarkOnElementOrSelection(marks.superscript));
    case "formatting":
      return run(view, clearFormatting);
    case "setFontSize":
      return run(view, setFontSize(event.data));
    case "setTextColor":
      return run(view, setColor(event.data || null));
    case "setHighlight":
      return run(view, setHighlight(event.data));
    case "setTextAlign":
      return run(view, setAlignment(event.data as Alignment));
    case "setTextDirection":
      return run(view, setTextDirection(event.data));
    case "setLineHeight":
      return run(view, setLineHeight(event.data));
    case "setTextStyle": {
      const style = toTextStyleId(event.data);
      if (style) run(view, setTextStyle(style));
      return;
    }
    case "setList": {
      const data = event.data as unknown as
        | ListChoice
        | { type: "icon"; data: { icon: string; unicode: string } };
      if (typeof data === "object") {
        setList(view, "icons", data.data);
      } else {
        setList(view, data);
      }
      return;
    }
    case "setLink": {
      const {
        status,
        url,
        type,
        event: analyticsEvent,
        openInPopup,
      } = event.data;
      if (status === "remove" || !url) return run(view, removeLink);
      return run(
        view,
        applyLink({ url, type, event: analyticsEvent, openInPopup }),
      );
    }
    case "insertIcon":
      return run(view, insertIcon(event.data));
    case "insertEmoji":
      return run(view, insertEmoji(event.data.emoji.shortcodes));
    case "saveRkpi":
      return run(view, saveRkpi(event.data));
    case "deleteRkpi":
      return run(view, deleteRkpi);
    case "setTextAnimation":
      return run(view, setTextAnimation(event.data));
    // Inside a repeatable group the token is namespaced (and registered as a
    // field) — the same choke point Froala's handler uses.
    case "setDynamicVariable":
      return run(
        view,
        insertVariableToken(namespaceInsertedToken(event.editorId, event.data)),
      );
    case "setDynamicVariableAt": {
      // A variable dropped onto the open editor: land it at the drop caret.
      let at: number | undefined;
      try {
        at = view.posAtDOM(
          event.data.range.startContainer,
          event.data.range.startOffset,
        );
      } catch {
        // The caret isn't inside this editor any more — use the selection.
      }
      return run(
        view,
        insertVariableToken(
          namespaceInsertedToken(event.editorId, event.data.token),
          at === undefined ? undefined : { from: at, to: at },
        ),
      );
    }
    case "setAIClassification":
      return run(view, setAiClassification(event.data || null));
    case "aiInsertText":
      return run(view, insertAiText(event.data.text));
    default:
      return;
  }
};
